// f:\checkin\frontend\src\screens\LoginSupplierScreen\LoginSupplierScreen.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import Logo from '../../components/Branding/Logo';
import TextInput from '../../components/FormElements/TextInput';

import { useAuth } from '../../contexts/AuthContext'; // Import useAuth
function LoginSupplierScreen() {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loginError, setLoginError] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [loginAttempted, setLoginAttempted] = useState(false);
  const [initialAuthCheckDone, setInitialAuthCheckDone] = useState(false);
  const [initialNavDecisionMade, setInitialNavDecisionMade] = useState(false);
  const [navigationCmd, setNavigationCmd] = useState(null);
  const { login, logout, isAuthenticated, role: authRole } = useAuth(); // user and authIsLoading removed

  // To track if the component is mounted, useful for async operations in useEffect
  const isMounted = useRef(true);
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);


  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    console.log('[LoginSupplierScreen handleSubmit] Start. Current isAuthenticated (from useAuth):', isAuthenticated);
    logout(); // Clear any existing session
    console.log('[LoginSupplierScreen handleSubmit] Called logout().');

    setIsSubmitting(true);
    setLoginAttempted(false); // Reset loginAttempted before a new attempt
    setLoginError(null);
    console.log('[LoginSupplierScreen handleSubmit] Supplier Login attempt with:', formData);

    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

    try {
      const response = await fetch(`${API_BASE_URL}/api/suppliers/login`, { // Assuming /api/suppliers/login
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      let loginData;
      const responseText = await response.text(); // Read raw response text first
      try {
        loginData = JSON.parse(responseText); // Attempt to parse as JSON
      } catch (parseError) {
        console.error('[LoginSupplierScreen handleSubmit] Failed to parse JSON response:', responseText);
        throw new Error(`Server returned non-JSON response: ${responseText.substring(0, 200)}`);
      }

      if (!response.ok) {
        const errorMessage = loginData.error?.message || loginData.message || `Login failed with status: ${response.status}`;
        if (isMounted.current) {
          setLoginError(errorMessage); // Set error message before throwing
        }
        throw new Error(errorMessage); // This will be caught by the outer catch
      }

      if (loginData.token && loginData.role) {
        console.log(`[LoginSupplierScreen handleSubmit] Login API success. Role: ${loginData.role}. Fetching supplier details...`);
        try {
          // IMPORTANT: Adjust this endpoint to your actual supplier details endpoint
          // It might be /suppliers/me or /suppliers/{supplierId}
          // For this example, I'll assume /suppliers/me similar to users/me
          const supplierDetailsResponse = await fetch(`${API_BASE_URL}/api/suppliers/me`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${loginData.token}`,
            },
          });

          const fetchedSupplierData = await supplierDetailsResponse.json();

          if (!supplierDetailsResponse.ok) {
            const supplierErrorMessage = fetchedSupplierData.error?.message || fetchedSupplierData.message || `Failed to fetch supplier details: ${supplierDetailsResponse.status}`;
            if (isMounted.current) {
              setLoginError(supplierErrorMessage);
            }
            throw new Error(supplierErrorMessage); // This will be caught by the outer catch
          }

          console.log('[LoginSupplierScreen handleSubmit] Supplier details fetched:', fetchedSupplierData);

          // Assuming the response structure is { supplier: { ... } } or just { ... }
          const supplierObject = fetchedSupplierData.supplier || fetchedSupplierData;

          // Prepare the supplier object for AuthContext
          // Adjust fields as necessary based on your supplier data model
          const authSupplierObject = {
            ...supplierObject,
            // Ensure 'id', 'name', 'email', 'avatar_file_id' (or similar) are present if your ProfileSupplierScreen expects them
            // Example: name might be supplierObject.company_name
            // Add image construction logic similar to LoginUserScreen
            image: null, // Default to null
          };

          // Construct image URL if avatar_file_id or similar field exists
          // Assuming supplierObject might have 'avatar_file_id' like users
          if (supplierObject && supplierObject.avatar_file_id) {
            const API_BASE_URL_FOR_IMAGE = process.env.REACT_APP_API_BASE_URL;
            authSupplierObject.image = `${API_BASE_URL_FOR_IMAGE}/files/serve/${supplierObject.avatar_file_id}`;
          } else if (supplierObject && supplierObject.avatar_filepath) { // Fallback for filepath if used
            authSupplierObject.image = supplierObject.avatar_filepath; // Assuming it's a full URL or relative to domain
          }

          // Call context login
          login(loginData.token, loginData.role, supplierObject.id, authSupplierObject);
          console.log(`[LoginSupplierScreen handleSubmit] Called context login(). isAuthenticated should become true in context.`);
          if (isMounted.current) { // Check if component is still mounted
            setLoginAttempted(true); // Signal that login process is complete for the useEffect
            console.log('[LoginSupplierScreen handleSubmit] Set loginAttempted to true.');
          }

        } catch (fetchSupplierError) {
          console.error('[LoginSupplierScreen handleSubmit] Error fetching supplier details after login:', fetchSupplierError.message);
          if (isMounted.current) {
            setLoginError(fetchSupplierError.message || 'Failed to fetch supplier data after login.');
          }
          // No throw here, error is set, finally will run.
        }
      } else {
        if (isMounted.current) {
          setLoginError('Login successful, but token or role not received from server.');
        }
        // No throw here, error is set, finally will run.
      }
    } catch (error) {
      console.error('[LoginSupplierScreen handleSubmit] Catch block error:', error.message);
      if (isMounted.current && !loginError) { // Only set if not already set by specific error handling
        setLoginError(error.message || 'Login failed. Please check your credentials.');
      }
    } finally {
      if (isMounted.current) {
        setIsSubmitting(false);
        console.log('[LoginSupplierScreen handleSubmit] Finally block. isSubmitting set to false.');
      }
    }
  };

  // Effect 1: Mark that the component has mounted and initial auth state can be checked.
  useEffect(() => {
    if (isMounted.current && !initialAuthCheckDone) {
      console.log('[LoginSupplierScreen Effect 1] Component mounted. Setting initialAuthCheckDone.');
      setInitialAuthCheckDone(true);
    }
  }, [initialAuthCheckDone]);

  // Effect 2: Decide on initial navigation if user is already authenticated as supplier.
  // Simplified navigation effect
  useEffect(() => {
    if (isMounted.current && isAuthenticated && authRole === 'supplier') {
      console.log('[LoginSupplierScreen] Authenticated as supplier, checking for preserved location');

      // Check if there's a preserved location from ProtectedRoute redirect
      const from = location.state?.from?.pathname || location.state?.from;
      if (from && from !== '/login') {
        console.log('[LoginSupplierScreen] Found preserved location from ProtectedRoute:', from);
        navigate(from, { replace: true });
        return;
      }

      console.log('[LoginSupplierScreen] No preserved location found, redirecting to /home');
      navigate('/home');
    }
  }, [isAuthenticated, authRole, navigate, location.state]);

  // Effect to handle successful login
  useEffect(() => {
    if (isMounted.current && loginAttempted && isAuthenticated && authRole === 'supplier') {
      console.log('[LoginSupplierScreen] Login successful, checking for preserved location');

      // Check if there's a preserved location from ProtectedRoute redirect
      const from = location.state?.from?.pathname || location.state?.from;
      if (from && from !== '/login') {
        console.log('[LoginSupplierScreen] Found preserved location from ProtectedRoute:', from);
        navigate(from, { replace: true });
        setLoginAttempted(false);
        return;
      }

      console.log('[LoginSupplierScreen] No preserved location found, redirecting to /home');
      navigate('/home');
      setLoginAttempted(false);
    }
  }, [loginAttempted, isAuthenticated, authRole, navigate, location.state]);




  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div className="col-md-6 form-side">
      <div className="form-container text-center">
        <Logo />
        <h1 className="text-success fw-bold mb-4">¡Bienvenido/a!</h1>
        {loginError && <div className="alert alert-danger" role="alert">{loginError}</div>}
        <form onSubmit={handleSubmit} noValidate>
          <TextInput
            type="email"
            id="email"
            name="email"
            placeholder="Email"
            ariaLabel="Email"
            value={formData.email}
            onChange={handleChange}
            required
            disabled={isSubmitting}
            iconClass="bi bi-envelope"
            wrapperClassName="mb-3"
          />
          <TextInput
            type={showPassword ? "text" : "password"}
            id="password"
            name="password"
            placeholder="Contraseña"
            ariaLabel="Contraseña"
            value={formData.password}
            onChange={handleChange}
            required
            disabled={isSubmitting}
            iconClass={`bi ${showPassword ? 'bi-eye' : 'bi-eye-slash'}`}
            onIconClick={togglePasswordVisibility}
            iconAriaLabel="Mostrar u ocultar contraseña"
            wrapperClassName="mb-0"
          />
          <div className="text-end mt-2"><Link to="/forgot-password" className="small">¿Olvidaste tu contraseña?</Link></div> {/* Changed from restore-password.html */}

          <div className="buttons-container d-grid gap-3 mt-5 mb-5">
            <button type="submit" className="btn btn-checkin" disabled={isSubmitting}>
              {isSubmitting ? 'Iniciando...' : 'Iniciar sesión'}
            </button>
          </div>
        </form>
        <p className="mt-3 text-muted small">protegido por reCAPTCHA · <a href="#">Privacidad</a> · <a href="#">Condiciones</a></p>
      </div>
    </div>
  );
}

export default LoginSupplierScreen;